import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    Tabs,
    Tab,
    TablePagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { handleUnauthorized } from '../../TokenAuth/auth';

function TicketSubjectList() {
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState('both');

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalDocs, setTotalDocs] = useState(0);

    // Form state
    const [title, setTitle] = useState('');
    const [targetApp, setTargetApp] = useState('both');

    const token = localStorage.getItem("astroVani-admin-token");
    const baseUrl = process.env.REACT_APP_BASEURL;

    useEffect(() => {
        setPage(0);
    }, [activeTab]);

    useEffect(() => {
        fetchSubjects();
    }, [activeTab, page, rowsPerPage]);

    const fetchSubjects = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${baseUrl}/admin/ticket-subject?targetApp=${activeTab}&page=${page + 1}&limit=${rowsPerPage}`, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json',
                },
            });
            if (response.data.success) {
                setSubjects(response.data.data || []);
                setTotalDocs(response.data.paginationDetail?.totalDocs || response.data.data?.length || 0);
            } else {
                toast.error(response.data.message || 'Failed to fetch ticket subjects');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error('Error fetching ticket subjects:', error);
                toast.error(error.response?.data?.message || 'Error fetching ticket subjects');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddSubmit = async () => {
        if (!title.trim()) {
            toast.error('Subject title is required');
            return;
        }

        try {
            setSaving(true);
            const payload = { title, targetApp };
            const headers = {
                'Authorization': token,
                'Content-Type': 'application/json',
            };

            let response;
            if (editingId) {
                response = await axios.patch(`${baseUrl}/admin/ticket-subject/${editingId}`, payload, { headers });
            } else {
                response = await axios.post(`${baseUrl}/admin/ticket-subject`, payload, { headers });
            }

            if (response.data.success) {
                toast.success(editingId ? 'Ticket subject updated successfully' : 'Ticket subject created successfully');
                handleCloseModal();
                fetchSubjects();
            } else {
                toast.error(response.data.message || (editingId ? 'Failed to update ticket subject' : 'Failed to create ticket subject'));
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error('Error creating ticket subject:', error);
                toast.error(error.response?.data?.message || 'Error creating ticket subject');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this ticket subject?")) return;

        try {
            setDeletingId(id);
            const response = await axios.delete(`${baseUrl}/admin/ticket-subject/${id}`, {
                headers: {
                    'Authorization': token,
                },
            });

            if (response.data.success) {
                toast.success('Ticket subject deleted successfully');
                fetchSubjects();
            } else {
                toast.error(response.data.message || 'Failed to delete ticket subject');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error('Error deleting ticket subject:', error);
                toast.error(error.response?.data?.message || 'Error deleting ticket subject');
            }
        } finally {
            setDeletingId(null);
        }
    };

    const handleOpenAddModal = () => {
        setEditingId(null);
        setTitle('');
        setTargetApp('user');
        setAddModalOpen(true);
    };

    const handleOpenEditModal = (row) => {
        setEditingId(row._id);
        setTitle(row.title || '');
        setTargetApp(row.targetApp || 'user');
        setAddModalOpen(true);
    };

    const handleCloseModal = () => {
        if (!saving) {
            setAddModalOpen(false);
            setEditingId(null);
            setTitle('');
            setTargetApp('user');
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <div style={{ padding: '24px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Ticket Subject List</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenAddModal}
                >
                    Add Subject
                </Button>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} textColor="primary" indicatorColor="primary">
                    <Tab label="Both" value="both" />
                    <Tab label="User" value="user" />
                    <Tab label="Astrologer" value="astro" />
                </Tabs>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>S.No.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Target App</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && subjects.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : subjects.length > 0 ? (
                                subjects.map((row, index) => (
                                    <TableRow hover key={row._id}>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{row.targetApp == "astro" ? "Astrologer" : row.targetApp}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleOpenEditModal(row)}
                                                disabled={deletingId === row._id}
                                                sx={{ mr: 1 }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDelete(row._id)}
                                                disabled={deletingId === row._id}
                                            >
                                                {deletingId === row._id ? <CircularProgress size={24} /> : <DeleteIcon />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        No ticket subjects found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalDocs}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={addModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? 'Edit Ticket Subject' : 'Add New Ticket Subject'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Title"
                            fullWidth
                            variant="outlined"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            disabled={saving}
                        />
                        <FormControl fullWidth disabled={saving}>
                            <InputLabel>Target App</InputLabel>
                            <Select
                                value={targetApp}
                                label="Target App"
                                onChange={(e) => setTargetApp(e.target.value)}
                            >
                                <MenuItem value="both">Both</MenuItem>
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="astro">Astrologer</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseModal} color="secondary" disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddSubmit} variant="contained" color="primary" disabled={saving}>
                        {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default TicketSubjectList;
